import React, { useRef, useState } from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Cta from "@/components/ui-kit/Cta";
import { submitAgencyLead } from "@/lib/api";
import { trackFormSubmit } from "@/lib/analytics";

const CLIENT_RANGES = ["1 to 10", "11 to 50", "51 to 200", "More than 200"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const truncate = (s, n = 40) => (s.length > n ? `${s.slice(0, n)}...` : s);

export default function AgencyLeadForm() {
  const [form, setForm] = useState({ name: "", email: "", firm: "", numClients: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [serverError, setServerError] = useState("");
  const honeypot = useRef("");

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target ? e.target.value : e }));

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (form.firm.trim().length < 2) next.firm = "Please enter your firm name.";
    if (!form.numClients) next.numClients = "Select a range.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("loading");
    try {
      await submitAgencyLead({
        name: form.name.trim(),
        email: form.email.trim(),
        firm: form.firm.trim(),
        numClients: form.numClients,
        message: form.message.trim(),
        honeypot: honeypot.current,
      });
      trackFormSubmit("agency-walkthrough");
      setStatus("done");
    } catch {
      setStatus("error");
      setServerError("We could not send your request just now. Your details are safe. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div className="rounded-3xl border border-etap-mint-300 bg-etap-mint-100 p-8 text-center" data-testid="agency-lead-success">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-etap-deep-700 shadow-etap-sm">
          <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
        </span>
        <h3 className="mt-4 font-display text-2xl font-bold text-etap-deep-800">Request received</h3>
        <p className="mx-auto mt-2 max-w-sm text-etap-deep-700">
          Thanks, {truncate(form.name.trim())}. Our agency team will reach out to schedule your walkthrough.
        </p>
      </div>
    );
  }

  const errCls = "mt-1.5 flex items-center gap-1.5 text-sm text-red-600";
  const inputCls = "h-12 rounded-xl border-etap-deep-100 bg-white";

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-etap-deep-100 bg-white p-6 shadow-etap-lg sm:p-8" noValidate data-testid="agency-lead-form">
      {/* Honeypot */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        onChange={(e) => {
          honeypot.current = e.target.value;
        }}
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="lead-name" className="text-etap-deep-700">Name</Label>
          <Input id="lead-name" value={form.name} maxLength={120} onChange={set("name")} className={`mt-2 ${inputCls}`} data-testid="lead-name" placeholder="Your name" />
          {errors.name && <p className={errCls} data-testid="lead-name-error"><AlertCircle className="h-4 w-4" />{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="lead-email" className="text-etap-deep-700">Email</Label>
          <Input id="lead-email" type="email" value={form.email} maxLength={140} onChange={set("email")} className={`mt-2 ${inputCls}`} data-testid="lead-email" placeholder="you@firm.com" />
          {errors.email && <p className={errCls} data-testid="lead-email-error"><AlertCircle className="h-4 w-4" />{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="lead-firm" className="text-etap-deep-700">Firm</Label>
          <Input id="lead-firm" value={form.firm} maxLength={120} onChange={set("firm")} className={`mt-2 ${inputCls}`} data-testid="lead-firm" placeholder="Firm name" />
          {errors.firm && <p className={errCls} data-testid="lead-firm-error"><AlertCircle className="h-4 w-4" />{errors.firm}</p>}
        </div>
        <div>
          <Label htmlFor="lead-clients" className="text-etap-deep-700">Number of clients</Label>
          <Select value={form.numClients} onValueChange={(v) => setForm((p) => ({ ...p, numClients: v }))}>
            <SelectTrigger id="lead-clients" className={`mt-2 ${inputCls}`} data-testid="lead-clients">
              <SelectValue placeholder="Select a range" />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_RANGES.map((r) => (
                <SelectItem key={r} value={r} data-testid={`lead-clients-${r.replace(/\s/g, "-")}`}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.numClients && <p className={errCls} data-testid="lead-clients-error"><AlertCircle className="h-4 w-4" />{errors.numClients}</p>}
        </div>
      </div>

      <div className="mt-5">
        <Label htmlFor="lead-message" className="text-etap-deep-700">Message (optional)</Label>
        <Textarea id="lead-message" value={form.message} maxLength={2000} onChange={set("message")} className="mt-2 min-h-28 rounded-xl border-etap-deep-100 bg-white" data-testid="lead-message" placeholder="Tell us a little about your firm and clients." />
      </div>

      {serverError && (
        <p className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700" data-testid="lead-server-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {serverError}
        </p>
      )}

      <div className="mt-6">
        <Cta onClick={onSubmit} variant="primary" size="lg" className="w-full sm:w-auto" testId="lead-submit">
          {status === "loading" ? "Sending..." : "Book a Walkthrough"}
        </Cta>
      </div>
      <p className="mt-3 text-xs text-etap-deep-400">
        We use your details only to arrange your walkthrough. No spam.
      </p>
    </form>
  );
}
